import { useQuery } from '@tanstack/react-query';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import type { AppEpreuve, AppEpreuveExercise } from '@/lib/student-objectives/objective-types';

export type AdminEpreuveSummary = AppEpreuve & { exercise_count: number };

export type EpreuveJobStatus =
  | 'queued'
  | 'processing'
  | 'done'
  | 'failed'
  | 'abandoned'
  | 'cancelled';

export interface AdminEpreuveJob {
  id: string;
  exerciseId: string;
  exerciseTitle: string;
  status: EpreuveJobStatus;
  currentStep: string | null;
  error: string | null;
  restartCount: number;
}

/**
 * Jobs de la queue d'extraction d'une épreuve (1 par exercice) — pour afficher
 * l'avancement et, surtout, le DÉTAIL des erreurs par exercice.
 * Rafraîchi pendant que le pipeline tourne.
 */
export function useAdminEpreuveJobs(epreuveId: string, isRunning: boolean) {
  return useQuery({
    queryKey: ['admin-epreuve-jobs', epreuveId],
    enabled: !!epreuveId,
    refetchInterval: isRunning ? 4000 : false,
    queryFn: async (): Promise<AdminEpreuveJob[]> => {
      const { data, error } = await supabase
        .from('epreuve_jobs')
        .select('id, exercise_id, status, current_step, error, restart_count, exercises(title)')
        .eq('epreuve_id', epreuveId)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      type Row = {
        id: string;
        exercise_id: string;
        status: EpreuveJobStatus;
        current_step: string | null;
        error: string | null;
        restart_count: number;
        exercises: { title: string } | null;
      };
      return ((data as unknown as Row[]) ?? []).map((r) => ({
        id: r.id,
        exerciseId: r.exercise_id,
        exerciseTitle: r.exercises?.title ?? 'Exercice',
        status: r.status,
        currentStep: r.current_step,
        error: r.error,
        restartCount: r.restart_count,
      }));
    },
  });
}

export function useAdminEpreuves() {
  return useQuery({
    queryKey: ['admin-epreuves'],
    queryFn: async (): Promise<AdminEpreuveSummary[]> => {
      const { data, error } = await supabase
        .from('epreuves')
        .select('*, epreuve_exercises(count)')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []).map((row) => ({
        id: row.id,
        title: row.title,
        description: row.description ?? null,
        subject: row.subject as SubjectSlug | null,
        level: row.level as SchoolLevel,
        examType: row.exam_type ?? null,
        examYear: row.exam_year ?? null,
        sourceFileUrl: row.source_file_url ?? null,
        ocr_status: row.ocr_status as AppEpreuve['ocr_status'],
        status: row.status as AppEpreuve['status'],
        exercise_count: (row.epreuve_exercises as unknown as { count: number }[])?.[0]?.count ?? 0,
      }));
    },
  });
}

export type EpreuvePipelineStatus = 'idle' | 'extracting' | 'generating' | 'done' | 'failed';

export interface AdminEpreuveDetail extends AppEpreuve {
  exercises: AppEpreuveExercise[];
  illustration_url: string | null;
  illustration_prompt: string | null;
  correction_file_url: string | null;
  pipeline_status: EpreuvePipelineStatus;
  pipeline_error: string | null;
  jobs_total: number;
  jobs_done: number;
}

export function useAdminEpreuveDetail(id: string) {
  return useQuery({
    queryKey: ['admin-epreuve-detail', id],
    enabled: !!id,
    queryFn: async (): Promise<AdminEpreuveDetail | null> => {
      const { data, error } = await supabase
        .from('epreuves')
        .select(
          `*, epreuve_exercises(
            epreuve_id,
            exercise_id,
            display_order,
            exercises(id, title, difficulty, estimated_minutes)
          )`,
        )
        .eq('id', id)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;

      type EERow = {
        epreuve_id: string;
        exercise_id: string;
        display_order: number;
        exercises: { id: string; title: string; difficulty: string; estimated_minutes: number };
      };

      const exercises: AppEpreuveExercise[] = ((data.epreuve_exercises as unknown as EERow[]) ?? [])
        .sort((a, b) => a.display_order - b.display_order)
        .map((ee) => ({
          epreuveId: ee.epreuve_id,
          exerciseId: ee.exercise_id,
          displayOrder: ee.display_order,
          exercise: {
            id: ee.exercises.id,
            title: ee.exercises.title,
            difficulty: ee.exercises.difficulty as AppEpreuveExercise['exercise']['difficulty'],
            estimatedMinutes: ee.exercises.estimated_minutes,
          },
        }));

      return {
        id: data.id,
        title: data.title,
        description: data.description ?? null,
        subject: data.subject as SubjectSlug | null,
        level: data.level as SchoolLevel,
        examType: data.exam_type ?? null,
        examYear: data.exam_year ?? null,
        sourceFileUrl: data.source_file_url ?? null,
        ocr_status: data.ocr_status as AppEpreuve['ocr_status'],
        status: data.status as AppEpreuve['status'],
        illustration_url: (data as any).illustration_url ?? null,
        illustration_prompt: (data as any).illustration_prompt ?? null,
        correction_file_url: data.correction_file_url ?? null,
        pipeline_status: (data.pipeline_status ?? 'idle') as EpreuvePipelineStatus,
        pipeline_error: data.pipeline_error ?? null,
        jobs_total: data.jobs_total ?? 0,
        jobs_done: data.jobs_done ?? 0,
        exercises,
      };
    },
  });
}
